import PaymentMode from '@/shared/enums/PaymentMode'
import CheckoutConfig from '@/shared/models/CheckoutConfig'
import { dateToServer } from '../utils/format'
import Address from './Address'
import Payment from './Payment'
import Person from './Person'
import Subscription from './Subscription'

export default class CheckoutForm {
  readonly contractId: string
  readonly notificationUrl?: string
  readonly supportText?: string
  readonly referenceId: string
  readonly disableAddress?: boolean
  readonly partialAddress?: boolean
  readonly payment: Payment
  readonly person: Person
  readonly address: Address
  readonly redirectCheckout?: boolean
  readonly subscription?: Subscription
  readonly checkoutId?: string

  constructor(config: CheckoutConfig) {
    this.notificationUrl = config.notificationUrl
    this.supportText = config.supportText
    this.contractId = config.contractId
    this.referenceId = config.referenceId
    this.disableAddress = config.lightboxDisableAddress ?? true
    this.partialAddress = config.lightboxPartialAddress
    this.payment = new Payment(config)
    this.person = new Person(config)
    this.address = new Address(config)
    this.redirectCheckout = config.redirectCheckout ?? false
    this.checkoutId = config.checkoutId

    if (config.subscription) {
      this.subscription = new Subscription(config)
    }
  }

  toServer(attemptReference?: string, useExternal3ds?: boolean) {
    const { person, address, payment } = this

    /* eslint-disable @typescript-eslint/camelcase */
    return {
      contract_id: this.contractId,
      notification_url: this.notificationUrl,
      support_text: this.supportText,
      reference_id: this.referenceId,
      checkout_id: this.checkoutId,
      disable_address: this.disableAddress || payment.pixDirect,
      attempt_reference: attemptReference,
      external_3ds: useExternal3ds ?? false,
      redirect_checkout: this.redirectCheckout ?? false,
      person: {
        first_name: person.firstName,
        surname: person.surname,
        birth_date: person.birthDate ? dateToServer(person.birthDate) : null,
        tax_id: person.taxId || null,
        tax_id_type: person.taxIdType || null,
        type: person.type || null,
        email: person.email,
        phone_country: person.phoneCountry || null,
        phone_area: person.phoneArea || null,
        phone: person.phone || null,
      },
      address: {
        main: address.main,
        number: address.number,
        additional: address.additional,
        locality: address.locality,
        city: address.city,
        state: address.state,
        country: address.country,
        zip_code: address.zipCode,
      },
      payment: {
        method: payment.method,
        amount: payment.amount,
        asset: payment.asset,
        process_payment: payment.processPayment,
        delay_capture: payment.delayCapture,
        save_card: payment.saveCard,
        soft_descriptor: payment.softDescriptor,
        country: payment.country,
        currency: payment.currency,
        ...(payment.card && {
          card: {
            number: payment.card.number,
            cvv: payment.card.cvv,
            holder: payment.card.holder,
            month: payment.card.month,
            year: payment.card.year,
            installments: payment.card.installments,
          },
        }),
        ...(payment.pix && {
          pix: {
            expiration_date: payment.pix.expirationDate,
          },
        }),
        ...(payment.bankSlip && {
          boleto: {
            expiration_date: payment.bankSlip.expirationDate,
            line1: payment.bankSlip.line1,
            line2: payment.bankSlip.line2,
            line3: payment.bankSlip.line3,
            logo: payment.bankSlip.logo,
          },
        }),
        ...(payment.oxxo && {
          oxxo: {
            expiration_date: payment.oxxo.expirationDate,
            description: payment.oxxo.description,
            logo: payment.oxxo.logo,
          },
        }),
        ...(payment.tiendasYFarmacias && {
          tiendas_y_farmacias: {
            expiration_date: payment.tiendasYFarmacias.expirationDate,
            description: payment.tiendasYFarmacias.description,
            logo: payment.tiendasYFarmacias.logo,
          },
        }),
        ...(payment.spei && {
          spei: {
            description: payment.spei.description,
            logo: payment.spei.logo,
          },
        }),
        ...(payment.paycash && {
          paycash: {
            expiration_date: payment.paycash.expirationDate,
          },
        }),
        ...(payment.picpay && {
          picpay: {
            expiration_date: payment.picpay.expirationDate,
          },
        }),
        ...(payment.bankTransfer && {
          bank_transfer: {
            description: payment.bankTransfer.description,
            bank_id: payment.bankTransfer.bankId ?? undefined,
            debin_alias: payment.bankTransfer.debinAlias ?? undefined,
          },
        }),
        ...(payment.pagoEfectivo && {
          pago_efectivo: {
            expiration_date: payment.pagoEfectivo.expirationDate,
          },
        }),
        ...(payment.deuna && {
          deuna: {
            expiration_date: payment.deuna.expirationDate,
          },
        }),
        ...(payment.nequi && {
          nequi: {
            expiration_date: payment.nequi.expirationDate,
          },
        }),
        ...(payment.mach && {
          mach: {
            expiration_date: payment.mach.expirationDate,
          },
        }),
        ...(payment.qrInteroperable && {
          qr_interoperable: {
            expiration_date: payment.qrInteroperable.expirationDate,
          },
        }),
        ...(payment.breb && {
          breb: {
            expiration_date: payment.breb.expirationDate,
          },
        }),
      },
      subscription: this.subscription && {
        reference_id: this.subscription.referenceId,
        scheme: this.subscription.scheme,
        retry_policy: this.subscription.retryPolicy,
        asset: this.subscription.asset,
        amount: this.subscription.amount,
        amount_type: this.subscription.amountType,
        periodicity: this.subscription.periodicity,
        initial_charge: this.subscription.initialCharge,
        merchant_initiation: this.subscription.merchantInitiation,
        due_date: this.subscription.dueDate,
        end_date: this.subscription.endDate,
        force_work_day: this.subscription.forceWorkDay,
        notification_url: this.subscription.notificationUrl,
        description: this.subscription.description,
        minimum_amount: this.subscription.minimumAmount,
        custom_period_type: this.subscription.customPeriodType,
        custom_period_count: this.subscription.customPeriodCount,
        expiration_date: this.subscription.expirationDate,
      }
    }
  }

  to3ds(attemptReference?: string) {
    const { person, address, payment } = this

    return {
      notificationUrl: this.notificationUrl,
      referenceId: this.referenceId,
      checkout_id: this.checkoutId,
      attemptReference,
      processPayment: payment.processPayment,
      saveCard: payment.saveCard,
      delayCapture: payment.delayCapture,
      redirectCheckout: this.redirectCheckout ?? false,
      paymentCountry: payment.country,
      paymentCurrency: payment.currency,
      customer: {
        id: person.taxId,
        taxIdType: person.taxIdType,
        name: `${person.firstName} ${person.surname}`,
        email: person.email,
        phones: [
          {
            country: person.phoneCountry ?? 55,
            area: person.phoneArea,
            number: person.phone?.replace(/\D/g, ''),
            type: 'MOBILE',
          },
        ],
      },
      paymentMethod: {
        type: payment.method === PaymentMode.creditCard ? 'CREDIT_CARD' : 'DEBIT_CARD',
        installments: payment.card?.installments,
        softDescriptor: payment.softDescriptor,
        card: {
          number: payment.card?.number,
          expMonth: payment.card?.month,
          expYear: payment.card?.year,
          cvv: payment.card?.cvv,
          holder: {
            name: payment.card?.holder,
          },
        },
      },
      amount: {
        value: (payment.amount * 100).toFixed(0), // centavos
        currency: payment.asset,
      },
      disableAddress: this.disableAddress || false,
      billingAddress: this.disableAddress ? undefined : {
        partialAddress: this.partialAddress || false,
        street: address.main,
        number: address.number,
        complement: address.additional,
        regionCode: address.state,
        country: payment.country !== 'BR' ? (address.country || payment.country) : 'BRA',
        city: address.city,
        postalCode: address.zipCode,
      },
      shippingAddress: this.disableAddress ? undefined : {
        partialAddress: this.partialAddress || false,
        street: address.main,
        number: address.number,
        complement: address.additional,
        regionCode: address.state,
        country: payment.country !== 'BR' ? (address.country || payment.country) : 'BRA',
        city: address.city,
        postalCode: address.zipCode,
      },
      subscription: this.subscription && {
        referenceId: this.subscription.referenceId,
        retryPolicy: this.subscription.retryPolicy,
        asset: this.subscription.asset,
        amount: this.subscription.amount,
        amountType: this.subscription.amountType,
        periodicity: this.subscription.periodicity,
        merchantInitiation: this.subscription.merchantInitiation,
        dueDate: this.subscription.dueDate,
        endDate: this.subscription.endDate,
        notificationUrl: this.subscription.notificationUrl,
        minimumAmount: this.subscription.minimumAmount,
        customPeriodType: this.subscription.customPeriodType,
        customPeriodCount: this.subscription.customPeriodCount,
      }
    }
  }

  toAnalyticsEvent(authenticated?: boolean) {
    const { payment } = this

    return {
      contract: this.contractId,
      disableAddress: this.disableAddress || false,
      method: payment.method,
      amount: payment.amount,
      asset: payment.asset,
      processPayment: payment.processPayment,
      delayCapture: payment.delayCapture,
      saveCard: payment.saveCard,
      pixDirect: payment.pixDirect,
      country: payment.country,
      currency: payment.currency,
      cardInstallments: payment.card?.installments ?? 0,
      authenticated: authenticated || false,
    }
  }
}
